<template>
  <VTextField
    v-model="emailInput"
    class="ns-email flex-column"
    name="email"
    :maxlength="maxLength"
    :label="label"
    :disabled="disabled"
    :rules="emailRules"
    validate-on-blur
    @blur="validateEmail"
  />
</template>

<script>
import { axios, axiosRetry } from '@nuskin/axios-util';
import { AuthenticationService } from '@nuskin/ns-account';
import { VTextField } from 'vuetify/lib';
const MAX_RETRIES = 3;

const myAxios = axios.create();
axiosRetry(myAxios, {
  retries: MAX_RETRIES,
  shouldResetTimeout: true,
  retryDelay: (retryCount) => {
    return retryCount * 200;
  },
});
export default {
  name: 'NsEmail',
  components: {
    VTextField,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default: '',
    },
    /**
     * This is the Label of the input component.
     * Example: "Email Address:"
     * For local translations of this for a variety of locales, see:
     * https://nuskin.atlassian.net/wiki/spaces/SLE/pages/379682861/Standard+Email+Component+Translations
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * This is the error message for the input component. It will not
     * actually be displayed unless the error state is reached.
     * Example: "Invalid e-mail"
     * For local translations, see the link above.
     */
    invalidEmailText: {
      type: String,
      required: true,
    },
    /**
     * This is the error message for the input component. It will not
     * actually be displayed unless the error state is reached.
     * Example: "Duplicate e-mail found"
     * For local translations, see the link above.
     */
    dupedEmailText: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * You can use your own api to validate emails by passing in a url. For example "https.testemail.com?email=josh@test.com". (You can of course retrieve the typed in email by listening to the 'input' event or by using v-model on NsEmail). Make sure to include validationServiceValidResponsePath.
     
    */
    validationServiceUrl: {
      type: String,
      default: '',
    },
    /**
     * This is the path for the valid property you are wanting from the service
     * ie data.valid
     */
    validationServiceValidResponsePath: {
      type: String,
      default: '',
    },
    /** Check for duplicates in okta */
    checkForDuplicates: {
      type: Boolean,
      default: false,
    },

    required: {
      type: Boolean,
      default: false,
    },
    requiredErrorMessage: {
      type: String,
      default: '',
    },
    maxLength: {
      type: Number,
      default: 241,
    },
  },
  data() {
    return {
      emailInput: this.value,
      isEmailAvailable: true,
      isServiceValid: true,
      isPatternVaild: true,
    };
  },
  computed: {
    emailRules() {
      return [
        ...(this.required
          ? [
              (value) => {
                return Boolean(value) || this.requiredErrorMessage;
              },
            ]
          : []),
        ...(this.validationServiceUrl
          ? [
              () => {
                return this.isEmailValid ? false : this.invalidEmailText;
              },
            ]
          : []),
        this.isEmailAvailable ? false : this.dupedEmailText,
        this.isPatternVaild ? false : this.invalidEmailText,
      ];
    },
    isEmailValid() {
      return (
        this.isPatternVaild && this.isServiceValid && this.isEmailAvailable
      );
    },
  },
  watch: {
    emailInput() {
      this.$emit('input', this.emailInput.toLowerCase().trim());
    },
    value(newValue) {
      this.emailInput = newValue;
    },
  },
  methods: {
    async validateEmail() {
      if (this.emailInput) {
        const pattern = /^[\s]*[A-Za-z0-9!#$%&*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?[\s]*$/;
        if (pattern.test(this.emailInput) === true) {
          this.isPatternVaild = true;
          if (this.validationServiceUrl) {
            await this.validateEmailUsingService();
          }
          if (this.checkForDuplicates) {
            this.validateDuplicateEmails();
          }
        } else {
          this.isPatternVaild = false;
        }
      } else {
        this.isPatternVaild = this.required ? false : true;
      }
      this.returnErrorState();
    },
    returnErrorState() {
      this.$emit('error-state', {
        invalidEmailText: this.invalidEmailText,
        isValid: !!this.isEmailValid,
      });
    },
    async validateEmailUsingService() {
      const response = await myAxios.get(this.validationServiceUrl);

      this.isServiceValid = this.getValueByKey(
        this.validationServiceValidResponsePath,
        response
      );
      if (this.isServiceValid) {
        this.isServiceValid = true;
      } else {
        this.isServiceValid = false;
      }
    },
    validateDuplicateEmails() {
      this.isEmailAvailable = true;
      if (this.checkForDuplicates) {
        AuthenticationService.isUsernameAvailable(
          this.emailInput,
          (error, response) => {
            if (!error) {
              this.isEmailAvailable = response.data.available;
            }
          }
        );
      }
    },
    getValueByKey(key, obj) {
      return key.split('.').reduce(function (a, b) {
        return a && a[b];
      }, obj);
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

.ns-email {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  position: relative;
}
</style>
